import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { clubsBookingApi, type StripeConnectStatus } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';

type StripeConnectStatusContextValue = {
  status: StripeConnectStatus | null;
  loading: boolean;
  loaded: boolean;
  error: string;
  isStripeReady: boolean;
  refreshStatus: (force?: boolean) => Promise<StripeConnectStatus | null>;
};

const StripeConnectStatusContext = createContext<StripeConnectStatusContextValue | undefined>(undefined);

type RefreshOptions = {
  force?: boolean;
};

export function StripeConnectStatusProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const inFlightRequestRef = useRef<Promise<StripeConnectStatus | null> | null>(null);

  const clearStatusState = useCallback(() => {
    setStatus(null);
    setLoading(false);
    setLoaded(false);
    setError('');
  }, []);

  const fetchStripeConnectStatus = useCallback(
    async (options?: RefreshOptions): Promise<StripeConnectStatus | null> => {
      const { force = false } = options || {};
      const user = auth.getUser();

      if (!user?.isAuthenticated || user.role === 'parent') {
        clearStatusState();
        return null;
      }

      if (inFlightRequestRef.current && !force) {
        return inFlightRequestRef.current;
      }

      setLoading(true);
      setError('');

      const requestPromise = (async () => {
        try {
          const nextStatus = await clubsBookingApi.getStripeConnectStatus();
          setStatus(nextStatus);
          setLoaded(true);
          return nextStatus;
        } catch (err: any) {
          setStatus(null);
          setLoaded(true);
          const messageText = err?.response?.data?.message;
          setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load Stripe status');
          return null;
        } finally {
          setLoading(false);
          inFlightRequestRef.current = null;
        }
      })();

      inFlightRequestRef.current = requestPromise;
      return requestPromise;
    },
    [clearStatusState],
  );

  useEffect(() => {
    void fetchStripeConnectStatus();
  }, [fetchStripeConnectStatus, pathname]);

  useEffect(() => {
    const user = auth.getUser();
    if (!user?.isAuthenticated || user.role === 'parent') {
      return undefined;
    }

    const refreshInterval = window.setInterval(() => {
      void fetchStripeConnectStatus();
    }, 60 * 1000);

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, [fetchStripeConnectStatus, pathname]);

  const value = useMemo<StripeConnectStatusContextValue>(
    () => ({
      status,
      loading,
      loaded,
      error,
      isStripeReady: Boolean(status?.hasAccount && status?.onboardingComplete && status?.chargesEnabled),
      refreshStatus: (force = false) => fetchStripeConnectStatus({ force }),
    }),
    [status, loading, loaded, error, fetchStripeConnectStatus],
  );

  return (
    <StripeConnectStatusContext.Provider value={value}>{children}</StripeConnectStatusContext.Provider>
  );
}

export function useStripeConnectStatus(): StripeConnectStatusContextValue {
  const context = useContext(StripeConnectStatusContext);
  if (!context) {
    throw new Error('useStripeConnectStatus must be used within StripeConnectStatusProvider');
  }
  return context;
}
